import {PlusOutlined} from '@ant-design/icons';
import {useUser} from '@dofleini/security';
import {ValidatorProvider} from '@dofleini/validator';
import {proposalrequest} from '@mep/pae-commons';
import {Button, Form} from 'antd';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useDialogsContext} from '@/contexts/DialogsContext';
import PartnerProposal
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal';
import ProposalRequestPartnerFormModal from '@/modules/proposalRequest/containers/ProposalRequestPartnerFormModal';
import {DIALOG_PARTNER_NAMESPACE} from '@/modules/proposalRequest/contants/proposalRequest';
import {handleIsSoleOwnerValue} from '@/modules/proposalRequest/hooks/useSocialDenominationType';

const {CNA} = proposalrequest;

const allowFields = [
  '_id',
  'id',
  'firstName',
  'middleName',
  'lastName',
  'secondName',
  'email',
  'phone',
  'metadata',
  'isRepresentative',
  'address',
  'gender',
  'bornPlace',
  'isTcp',
  'civilStatus',
  'idNumber',
  'dniPictures',
  'idNumberONAT',
  'onatPictures',
  'civilStatusInfo',
];

const ProposalPrivatePartners = ({
  value,
  onChange,
  notIncludeUser,
  addLabelText,
  descriptionText,
  isStatePartnerForm,
  limit,
  proposal,
  setProposal,
  cashEdit = true,
  allowAddPartners = false,
  isProcedure = false,
  allowFullChanges = false,
  isTCPProposal = false
}) => {
  const {openDialog} = useDialogsContext(DIALOG_PARTNER_NAMESPACE);
  const {user} = useUser();
  const {t} = useTranslation('proposalRequest');

  const isCNA = [CNA].includes(proposal?.subjectType);

  const representativePartner = useMemo(() => {
    const copy = pick(proposal?.owner || user, allowFields);
    return ({...copy, representative: true});
  }, [proposal?.owner, user]);

  let [data, setData] = useState(notIncludeUser ? [] : [representativePartner]);

  useEffect(() => {
    if (value) {
      const exist = notIncludeUser || value.some(e => e.id === representativePartner?.id || e.representative);
      if (exist) {
        setData([].concat(value || []));
      } else {
        const v = [representativePartner].concat(value || []);
        setData(v);
        onChange([...v]);
      }
    }
  }, [notIncludeUser, onChange, representativePartner, value]);

  const onAddRequest = useCallback(() => {
    openDialog();
  }, [openDialog]);

  const updateOrganizations = useCallback((data, idNumber, newIdNumber, isEdit) => {
    if (!isEdit) {
      return {
        representative: data?.representative?.idNumber === idNumber ? null : data?.representative,
        members: data?.members?.filter(e => e?.idNumber !== idNumber)
      };
    }

    return {
      representative: data?.representative?.idNumber === idNumber ? {
        ...data?.representative,
        idNumber: newIdNumber
      } : data?.representative,
      members: data?.members?.map(e => e?.idNumber === idNumber ? {...e, idNumber: newIdNumber} : e)
    };
  }, []);

  const updateResponsibility = useCallback((idNumber, newIdNumber, isEdit, partners) => {
    if (proposal?.inspectionControl) {
      proposal.inspectionControl = updateOrganizations(proposal?.inspectionControl, idNumber, newIdNumber, isEdit);
    }

    if (proposal?.administrationControl) {
      proposal.administrationControl = updateOrganizations(proposal?.administrationControl, idNumber, newIdNumber, isEdit);
    }

    setProposal?.({...proposal, partners});
  }, [proposal, setProposal, updateOrganizations]);

  const onAddPartner = useCallback(async (partner, isEdit, recordIndex) => {
    return new Promise((resolve, reject) => {
      try {
        if (!isEdit) {
          data.push(partner);
        } else {
          data[recordIndex] = partner;
          // check if this partner updated is on the responsibilities
          if (value[recordIndex]?.idNumber !== partner?.idNumber)
            updateResponsibility(value[recordIndex]?.idNumber, partner?.idNumber, isEdit, value);
        }
        setProposal?.({...proposal, partners: [...data]});
        setData([...data]);
        onChange([...data]);
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });
  }, [data, onChange, proposal, setProposal, updateResponsibility, value]);

  const onEditPartner = useCallback(async (record, i) => {
    openDialog({partner: {...record}, isEdit: true, recordIndex: i});
  }, [openDialog]);

  const onDeletePartner = useCallback(async (index) => {
    const copy = data[index];
    const wasDeleted = data.splice(index, 1);
    setData([...data]);
    onChange([...data]);
    // check if this deleted partner belong to the organizations section
    if (copy && wasDeleted)
      updateResponsibility(copy.idNumber, null, false, data);
  }, [data, onChange, updateResponsibility]);

  const onChangeValues = useCallback(async (record, index) => {
    data[index] = record;
    setData([...data]);
    onChange([...data]);
  }, [data, onChange]);

  const isSoleOwner = useMemo(() => handleIsSoleOwnerValue(proposal?.isSoleOwner, proposal?.owner?.isSoleOwner), [proposal?.isSoleOwner, proposal?.owner?.isSoleOwner]);

  return (
    <div className="mt-4">
      <ProposalRequestPartnerFormModal
        proposal={proposal}
        onConfirm={onAddPartner}
        titleText={addLabelText}
        isStatePartner={isStatePartnerForm}
        descriptionText={descriptionText}
        isTCPProposal={isTCPProposal}/>
      <div>
        {data?.map((e, i) => <PartnerProposal
          data={e}
          key={i}
          index={i}
          list={data}
          isCNA={isCNA}
          proposal={proposal}
          isStatePartnerForm={isStatePartnerForm}
          onEditPartner={() => onEditPartner(e, i)}
          onDeletePartner={() => onDeletePartner(i)}
          onChange={(y, index) => onChangeValues(y, index)}
          cashEdit={cashEdit}
          allowFullChanges={allowFullChanges}
          isProcedure={isProcedure}
          isTCPProposal={isTCPProposal}
        />)}
      </div>
      {!cashEdit ? null :
        (allowAddPartners && (isCNA || ((!limit || data?.length < limit) && !isSoleOwner))) &&
        <Button
          type={'primary'}
          onClick={onAddRequest}
          className={'cursor-pointer mb-2 bg-secondary border-secondary'}
          size={'large'} icon={<PlusOutlined/>}
        >
          {t(addLabelText || 'proposalRequest.form.partners.addText')}
        </Button>}
    </div>
  );
};

ProposalPrivatePartners.propTypes = {
  limit: PropTypes.number,
  value: PropTypes.array,
  proposal: PropTypes.object,
  setProposal: PropTypes.func,
  addLabelText: PropTypes.string,
  descriptionText: PropTypes.string,
  isStatePartnerForm: PropTypes.bool,
  notIncludeUser: PropTypes.bool,
  isTCPProposal: PropTypes.bool,
  allowAddPartners: PropTypes.bool,
  allowFullChanges: PropTypes.bool,
  isProcedure: PropTypes.bool,
  onChange: PropTypes.func,
  cashEdit: PropTypes.bool,
};

ProposalPrivatePartners.defaultProps = {};

export default memo(ProposalPrivatePartners);

export const renderProposalPrivatePartners = ({formItemProps, field, form}) => {
  const rules = field?.required && [{required: true, message: ValidatorProvider.t('validateMessages.required')}];

  return (
    <Form.Item {...formItemProps} required={field?.required} rules={rules}>
      <ProposalPrivatePartners {...field?.widgetProps} {...field} form={form}/>
    </Form.Item>
  );
};
